import DynamicImage from '@/components/commons/dynamic-image';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import {
  AboutUsStoreLocationElementDataType,
  StoreElementDataType,
} from '@/components/sections/builder/elements/body/store-location/type';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputLink from '@/components/sections/builder/settings/input-link';
import { ElementProps } from '@/components/sections/builder/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

import { cn, removeHTMLTags, truncateText } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { Plus, Store, Trash } from 'lucide-react';
import { shallow } from 'zustand/shallow';

const defaultData: StoreElementDataType = {
  name: 'Store',
  contents: [
    {
      heading: 'Title',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
    },
  ],
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as StoreElementDataType;
  const content = element.parentData as AboutUsStoreLocationElementDataType;

  return (
    <>
      <div className='flex flex-col gap-4 lg:gap-8'>
        <div
          className={cn(
            'overflow-hidden rounded-lg duration-300 hover:scale-[1.01]',
            content.imageRatio === 'adaptToImage' && 'img_ratio_adapt',
            content.imageRatio === 'portrait' && 'img_ratio_portrait',
            content.imageRatio === 'square' && 'img_ratio_square'
          )}
        >
          <DynamicImage
            src={data.image as string}
            alt='Image store'
            width={500}
            height={300}
            className='h-full w-full object-cover'
            sizes='100vw'
          />
        </div>
        <div className='flex flex-col gap-2 lg:gap-4'>
          {data.contents?.map((content, idx) => (
            <div key={idx}>
              <div
                className='text-secondary-store-location mb-1 text-base leading-6 tracking-[-1.1%]'
                dangerouslySetInnerHTML={{ __html: content.heading || '' }}
              />
              <div
                className='rich-text-store-location text-sm leading-6 tracking-[-1.1%]'
                dangerouslySetInnerHTML={{ __html: content.description || '' }}
              />
            </div>
          ))}
        </div>
      </div>
      <style jsx global>
        {`
          .img_ratio_adapt {
            display: flex;
            align-items: center;
            justify-content: center;
            aspect-ratio: auto;
          }
          .img_ratio_portrait {
            aspect-ratio: 3 / 4;
          }
          .img_ratio_square {
            aspect-ratio: 1 / 1;
          }
          .text-secondary-store-location {
            color: ${content.colorScheme?.textSecondary};
          }
          .rich-text-store-location a,
          .rich-text-store-location .link {
            color: ${content.colorScheme?.btnSolidBackgroud || 'blue'} !important;
            text-decoration: underline !important;
          }
        `}
      </style>
    </>
  );
};

export const ElementSetting = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as StoreElementDataType;
  const contents = data.contents || [];

  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const onChangeContent = (idx: number, key: string, val: string) => {
    const tempContents = [...(data.contents || [])];
    tempContents[idx] = {
      ...tempContents[idx],
      [key]: val,
    };
    builder.setChildData(element.parentId as string, element.id, {
      contents: tempContents,
    });
  };

  const onAddContent = () => {
    const tempContents = [...(data.contents || [])];
    tempContents.push({
      heading: 'Title',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
    });
    builder.setChildData(element.parentId as string, element.id, {
      contents: tempContents,
    });
  };

  const onRemoveContent = (idx: number) => {
    const tempContents = [...(data.contents || [])];
    tempContents.splice(idx, 1);
    builder.setChildData(element.parentId as string, element.id, {
      contents: tempContents,
    });
  };

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Image Content</h1>
      <ImageSetting
        value={data?.image}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            image: val,
          })
        }
      />
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Button Settings</h1>
      <h4 className=''>Text Label</h4>
      <TextEditorMinimal
        placeholder='Label'
        onChange={(content) => {
          if (content) {
            builder.setChildData(element.parentId as string, element.id, {
              buttonLabel: typeof content === 'string' ? content : content.toString(),
            });
          }
        }}
        value={data?.buttonLabel || ''}
        className='mb-4 w-full '
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-1',
          },
        }}
        editable
      />
      <h4 className=''>Button url</h4>
      <InputLink
        name='buttonUrl'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            buttonLink: value,
          });
        }}
        value={data.buttonLink as string}
      />
      <div className='my-2 flex items-center gap-2'>
        <label>Show Button</label>
        <Switch
          className='ml-auto'
          checked={data.showButton}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showButton: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='flex items-center gap-2 '>
        <label>Show Icon</label>
        <Switch
          className='ml-auto'
          checked={data.showIcon}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showIcon: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <Separator className='my-4' />
      {/* <span className='flex mt-4 mb-2 text-sm'>Content</span> */}
      <h1 className='text-sm font-bold '>Content</h1>
      <Accordion type='single' collapsible defaultValue='item-0' className='w-full'>
        {contents.map((content, idx) => (
          <AccordionItem value={`item-${idx}`} key={idx}>
            <AccordionTrigger className='py-2'>
              <div className='flex w-full items-center justify-between'>
                <p className='content-footer-text'>
                  {truncateText(removeHTMLTags(content.heading as string), 20)}
                </p>
                <Button
                  variant='ghost'
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveContent(idx);
                  }}
                  className='rounded px-1 py-0 text-red-500 hover:bg-red-100'
                >
                  <Trash className='h-4 w-4' />
                </Button>
              </div>
            </AccordionTrigger>
            <AccordionContent className='pl-4'>
              <div className='mb-3'>
                <h4 className='text-xs'>Heading</h4>
                <TextEditorMinimal
                  value={content.heading as string}
                  onChange={(val) => onChangeContent(idx, 'heading', val as string)}
                  output='html'
                  editorProps={{
                    attributes: {
                      class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
                    },
                  }}
                  className='mb-2 w-full '
                  editable
                />
              </div>
              <div className='mb-3'>
                <h4 className='text-xs'>Description</h4>
                <TextEditorMinimal
                  value={content.description as string}
                  onChange={(val) => onChangeContent(idx, 'description', val as string)}
                  output='html'
                  editorProps={{
                    attributes: {
                      class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
                    },
                  }}
                  className='mb-2 w-full '
                  editable
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <button
        type='button'
        onClick={onAddContent}
        className='flex w-full items-center justify-center gap-x-2 rounded-sm px-6 py-2 text-primary hover:bg-[#E9F7FF]'
      >
        <Plus className='h-4 w-4' />
        Add Content
      </button>
    </div>
  );
};

export const ElementPreviewIcon = Store;

const Address = {
  type: 'about_us_address',
  group: 'about_us_addresses',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  defaultChilds: [],
  childs: [],
};

export default Address;
